import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import adminConfig from '@/features/promotions/config.json';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { useExperiment } from '@/libraries/experiment';
import { MonetizationType, ObjectType } from '@/@types/graphql-types';
import { BuyboxPresentationType } from '@/stores/pinia/useUserStore';

export function useBuyboxView({ objectType }: { objectType?: MaybeRefOrGetter<ObjectType | undefined> }) {
	const { country } = storeToRefs(useLanguageStore());
	const { activeVariantWithControlGroup: gridSwiperVariant } = useExperiment('EXP_GRID_SWIPER');
	const { activeVariant: gridRedesignMoreInfoVariant } = useExperiment('EXP_GRID_REDESIGN_MORE_INFO_V2');
	const { activeVariantWithControlGroup: underperformingUrlsExp5 } = useExperiment('EXP_UNDERPERFORMING_URLS_5');

	const hasActiveGridBuyboxExperiment = computed(
		() => !!gridSwiperVariant.value || !!gridRedesignMoreInfoVariant.value || !!underperformingUrlsExp5.value
	);

	const buyboxConfig = adminConfig?.buybox;

	/* Settings from Config */
	const isGridDefaultFromConfig = computed(
		() =>
			// Default to grid if config value is invalid
			buyboxConfig?.buybox_default !== 'list'
	);

	const countryOverrides = computed(() => {
		const overrideKey = isGridDefaultFromConfig.value ? 'list_buybox_active' : 'grid_buybox_active';

		return buyboxConfig?.[overrideKey] ?? null;
	});

	const pageType = computed(() => {
		if (toValue(objectType) === ObjectType.ShowSeason) return 'season';
		if (toValue(objectType) === ObjectType.Show) return 'show';
		return 'movie';
	});

	/* Title Page Buybox View */
	const isGridBuyboxDefault = computed(() => {
		if (hasActiveGridBuyboxExperiment.value) return true;
		if (!countryOverrides.value || !toValue(objectType)) return isGridDefaultFromConfig.value;

		/* Country Overrides - always returns opposite of default from config */
		const hasActiveCountryOverride =
			// 1. check if there is a country override for current page type
			(countryOverrides.value[pageType.value] ?? []).includes(country.value) ||
			// 2. check if there is a country override for all page types
			(countryOverrides.value.all_titles ?? []).includes(country.value);

		return hasActiveCountryOverride ? !isGridDefaultFromConfig.value : isGridDefaultFromConfig.value;
	});

	return {
		isGridBuyboxDefault,
	};
}

export function useGridCategorySplit({
	objectType,
	buyBoxActiveType,
}: {
	objectType?: MaybeRefOrGetter<ObjectType | undefined>;
	buyBoxActiveType?: MaybeRefOrGetter<BuyboxPresentationType | null | undefined>;
} = {}) {
	const { country } = storeToRefs(useLanguageStore());
	const { isLoggedIn } = storeToRefs(useUserStore());

	const pageType = computed(() => {
		if (toValue(objectType) === ObjectType.ShowSeason) return 'season';
		if (toValue(objectType) === ObjectType.Show) return 'show';
		return 'movie';
	});

	const isGridCategorySplitActive = computed(() => {
		if (isLoggedIn.value) return false;
		if (toValue(buyBoxActiveType) === MonetizationType.Free) return false;
		const config = adminConfig?.buybox?.grid_category_split_active as Record<string, string[] | null> | undefined;
		if (!config || !toValue(objectType)) return false;
		return (
			(config[pageType.value] ?? []).includes(country.value) || (config.all_titles ?? []).includes(country.value)
		);
	});

	return { isGridCategorySplitActive };
}
